import { useEffect, useState } from "react";
import { ArrowLeft, Copy, Mail, MessageSquare, Share2 } from "lucide-react";

/** Set by the guest's stay page so the signing pages can send them back to it. */
export const STAY_RETURN_KEY = "stayReturn";

/**
 * Phones: the signing pages are long legal documents. While the signing form
 * is still below the fold, a pinned bar says what is left and jumps to it.
 * It never signs anything itself.
 */
export function StickySignBar({ targetId, title, detail, cta }: { targetId: string; title: string; detail: string; cta: string }) {
  const [targetBelow, setTargetBelow] = useState(false);

  useEffect(() => {
    const el = document.getElementById(targetId);
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setTargetBelow(!entry.isIntersecting && entry.boundingClientRect.top > 0),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [targetId]);

  if (!targetBelow) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-sand-deep bg-shell/95 px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-6px_20px_rgba(23,49,28,0.08)] backdrop-blur lg:hidden" data-testid="sticky-sign-bar">
      <div className="mx-auto flex max-w-md items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate font-sans text-[14px] font-medium text-ink">{title}</p>
          <p className="truncate font-sans text-[12px] text-ink-soft">{detail}</p>
        </div>
        <button
          type="button"
          onClick={() => document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "start" })}
          className="inline-flex min-h-[48px] shrink-0 items-center justify-center bg-sun px-5 font-sans text-[14px] font-medium text-shell hover:bg-sun-hover"
        >
          {cta}
        </button>
      </div>
    </div>
  );
}

/**
 * "Back to your stay page" on the waiver and Guest Release pages, shown only
 * when this browser came from the stay page for the same booking. Someone who
 * opened a shared signing link never sees it.
 */
export function StayReturnLink({ bookingId, className = "" }: { bookingId?: string; className?: string }) {
  const [href, setHref] = useState<string | null>(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STAY_RETURN_KEY);
      if (!raw || !bookingId) return;
      const saved = JSON.parse(raw) as { bookingId?: string; url?: string };
      if (saved.bookingId === bookingId && typeof saved.url === "string" && saved.url.startsWith("/my-stay/")) {
        setHref(saved.url);
      }
    } catch {
      setHref(null);
    }
  }, [bookingId]);

  if (!href) return null;
  return (
    <a
      href={href}
      className={`inline-flex min-h-[44px] items-center gap-2 font-sans text-[15px] text-forest underline decoration-sand-deep underline-offset-4 hover:text-ink ${className}`}
      data-testid="back-to-stay"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
      Back to your stay page
    </a>
  );
}

/**
 * Get the waiver link to the rest of the party in one tap. The booker is the
 * only person we can reach, so the link has to travel through whatever chat
 * the trip is already being planned in: the phone's share sheet where there
 * is one, otherwise a prefilled text or email, and always a copy button.
 */
export function ShareWaiverLink({ url, homeName, className = "", documentName = "water activity waiver" }: {
  url: string;
  homeName?: string;
  className?: string;
  /** What is being signed, in the shared message. */
  documentName?: string;
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const messageFor = (link: string) => `Please sign the ${documentName} for our stay${homeName ? ` at ${homeName}` : ""} before we arrive (everyone signs for themselves; it takes a minute or two): ${link}`;
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  async function share() {
    try {
      await navigator.share({ title: documentName.charAt(0).toUpperCase() + documentName.slice(1), text: message, url: absoluteUrl });
    } catch {
      /* the visitor dismissed the sheet */
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(absoluteUrl);
      setCopied(true);
      setCopyFailed(false);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
      setCopyFailed(true);
    }
  }

  const btn = "inline-flex min-h-[44px] items-center justify-center gap-2 whitespace-nowrap border border-sand-deep bg-shell px-3 py-2.5 font-sans text-[12px] uppercase tracking-[0.12em] text-forest transition-colors hover:bg-sand sm:px-4";
  // A pasted relative path means nothing in a group chat. Share the full address.
  const absoluteUrl = (() => {
    try {
      return typeof window !== "undefined" ? new URL(url, window.location.origin).toString() : url;
    } catch {
      return url;
    }
  })();
  const message = messageFor(absoluteUrl);

  return (
    <div className={className} data-testid="share-waiver-link">
      <p className="mb-2 font-sans text-[12px] uppercase tracking-[0.2em] text-driftwood">Share with the rest of your party</p>
      <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap">
        {canShare && (
          <button type="button" onClick={share} className={`${btn} col-span-3 border-sun bg-sun text-shell hover:bg-sun-hover`} data-testid="share-waiver-native">
            <Share2 className="h-4 w-4" aria-hidden="true" /> Share
          </button>
        )}
        <a href={`sms:?&body=${encodeURIComponent(message)}`} className={btn} data-testid="share-waiver-sms">
          <MessageSquare className="hidden h-4 w-4 shrink-0 min-[400px]:block" aria-hidden="true" /> Text it
        </a>
        <a href={`mailto:?subject=${encodeURIComponent(`${documentName.charAt(0).toUpperCase() + documentName.slice(1)} for our stay`)}&body=${encodeURIComponent(message)}`} className={btn} data-testid="share-waiver-email">
          <Mail className="hidden h-4 w-4 shrink-0 min-[400px]:block" aria-hidden="true" /> Email it
        </a>
        <button type="button" onClick={copy} className={btn} data-testid="share-waiver-copy">
          <Copy className="hidden h-4 w-4 shrink-0 min-[400px]:block" aria-hidden="true" /> {copied ? "Copied" : "Copy link"}
        </button>
      </div>
      <div className="mt-2 border border-sand-deep bg-shell px-3 py-2">
        <code className="line-clamp-2 max-w-full select-all break-all font-sans text-[12px] leading-relaxed text-ink-soft">{/^https?:\/\//i.test(url) ? url : absoluteUrl}</code>
      </div>
      {copyFailed && (
        <p className="mt-2 font-sans text-[13px] text-driftwood" role="alert">
          Copy was blocked by your browser. Press and hold the link above to copy it, or use Text it or Email it.
        </p>
      )}
    </div>
  );
}
